// Converts a consultation service's page `content` (a JSON object) to and from the
// admin form, so admins edit plain fields instead of raw JSON.
//
// `contentFromForm` starts from the existing content and only touches the keys the
// form knows about, so anything else a page carries (for example the mobile-number
// page's `sections`, `about`, `process` ...) is kept exactly as it was.

const FORM_KEYS = ["intro", "provide", "provideNote", "analysisSteps", "concernOptions", "receive", "closingHeading", "closingText"];

export const isPlainObject = (v) => typeof v === "object" && v !== null && !Array.isArray(v);

// one item per line
export const toLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
export const fromLines = (text) =>
  String(text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

// paragraphs are separated by a blank line
export const toParagraphs = (arr) => (Array.isArray(arr) ? arr.join("\n\n") : "");
export const fromParagraphs = (text) =>
  String(text || "")
    .split(/\n\s*\n/)
    .map((s) => s.trim())
    .filter(Boolean);

export const emptyBlock = () => ({ title: "", description: "", points: "", note: "" });
export const emptyStep = () => ({ title: "", description: "" });

export const formFromContent = (content) => {
  const c = isPlainObject(content) ? content : {};
  return {
    intro: toParagraphs(c.intro),
    provide: (Array.isArray(c.provide) ? c.provide : []).map((b) => ({
      title: b.title || "",
      description: b.description || "",
      points: toLines(b.points),
      note: b.note || "",
    })),
    provideNote: c.provideNote || "",
    analysisSteps: (Array.isArray(c.analysisSteps) ? c.analysisSteps : []).map((s) => ({
      title: s.title || "",
      description: s.description || "",
    })),
    concernOptions: toLines(c.concernOptions),
    receive: toLines(c.receive),
    closingHeading: c.closingHeading || "",
    closingText: c.closingText || "",
  };
};

const isBlank = (o) => Object.values(o).every((v) => !String(v).trim());

export const contentFromForm = (base, form) => {
  const next = { ...(isPlainObject(base) ? base : {}) };
  FORM_KEYS.forEach((k) => delete next[k]);

  const intro = fromParagraphs(form.intro);
  if (intro.length) next.intro = intro;

  const provide = form.provide
    .filter((b) => !isBlank(b))
    .map((b) => {
      const block = { title: b.title.trim(), description: b.description.trim(), points: fromLines(b.points) };
      if (b.note.trim()) block.note = b.note.trim();
      return block;
    });
  if (provide.length) next.provide = provide;
  if (form.provideNote.trim()) next.provideNote = form.provideNote.trim();

  const steps = form.analysisSteps
    .filter((s) => !isBlank(s))
    .map((s) => ({ title: s.title.trim(), description: s.description.trim() }));
  if (steps.length) next.analysisSteps = steps;

  const concernOptions = fromLines(form.concernOptions);
  if (concernOptions.length) next.concernOptions = concernOptions;
  const receive = fromLines(form.receive);
  if (receive.length) next.receive = receive;

  if (form.closingHeading.trim()) next.closingHeading = form.closingHeading.trim();
  if (form.closingText.trim()) next.closingText = form.closingText.trim();
  return next;
};

// The public page uses each block's / step's title as its list key, so titles must be filled and unique.
export const validateServiceForm = (form) => {
  const check = (items, label) => {
    const used = items.filter((i) => !isBlank(i));
    if (used.some((i) => !i.title.trim())) return `Every ${label} needs a title.`;
    const titles = used.map((i) => i.title.trim().toLowerCase());
    if (new Set(titles).size !== titles.length) return `Two ${label}s have the same title.`;
    return null;
  };
  return check(form.provide, "\"What we provide\" block") || check(form.analysisSteps, "analysis step");
};
